import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Ban,
  CalendarCheck,
  CalendarDays,
  CalendarX,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FileText,
  Loader2,
  LogOut,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { api, formatApiError, withRefreshRetry } from "../lib/api";
import { useContent } from "../lib/content";
import { TIME_SLOTS } from "../data/services";

const STATUS_STYLES = {
  new: "bg-gold/20 text-forest border-gold/50",
  contacted: "bg-olive/15 text-forest border-olive/40",
  confirmed: "bg-forest text-cream border-forest",
  closed: "bg-forest/5 text-sage border-forest/15",
};

const DOT = {
  new: "bg-gold",
  contacted: "bg-olive",
  confirmed: "bg-forest",
  closed: "bg-forest/30",
};

const inputClass =
  "w-full border-b border-forest/25 bg-transparent py-3 text-base text-forest placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-olive";

function formatPreferred(dateStr, timeStr) {
  if (!dateStr) return "";
  let pretty = dateStr;
  try {
    pretty = new Date(`${dateStr}T12:00:00`).toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    /* keep raw */
  }
  return timeStr ? `${pretty} — ${timeStr}` : pretty;
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-4">
          <Logo size={72} />
          <h1 className="font-display text-3xl font-medium text-forest">Team Login</h1>
          <p className="text-sm text-sage">Rooted Business Advisors — Admin</p>
        </div>
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="admin-login-form">
          <div>
            <label htmlFor="al-email" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
              Email
            </label>
            <input id="al-email" type="email" required data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="you@rootedbusinessadvisors.com" />
          </div>
          <div>
            <label htmlFor="al-password" className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
              Password
            </label>
            <input id="al-password" type="password" required data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="••••••••" />
          </div>
          {error && (
            <p className="text-sm text-red-700" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit-button"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
        <Link to="/" data-testid="admin-back-home-link" className="mt-8 block text-center text-xs text-sage transition-colors duration-300 hover:text-forest">
          ← Back to website
        </Link>
      </div>
    </div>
  );
}

const STRIPE = {
  backgroundImage: "repeating-linear-gradient(45deg, rgba(26,54,36,0.06) 0 6px, transparent 6px 12px)",
};

function CalendarView({ items, blocked, onConfirm, onBlock, onUnblock }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDate = {};
  for (const it of items) {
    if (it.preferred_date) {
      (byDate[it.preferred_date] = byDate[it.preferred_date] || []).push(it);
    }
  }
  const blockedSet = new Set(blocked);
  const cells = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const keyFor = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const now = new Date();
  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const noDates = items.filter((it) => !it.preferred_date).length;

  const dayItems = selectedDay ? byDate[selectedDay] || [] : [];
  const dayBlocked = selectedDay ? blockedSet.has(selectedDay) : false;

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          data-testid="calendar-prev-button"
          className="inline-flex items-center gap-1 rounded-full border border-forest/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
        >
          <ChevronLeft className="h-4 w-4" /> Prev
        </button>
        <h2 className="font-display text-3xl font-medium text-forest" data-testid="calendar-month-label">
          {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </h2>
        <button
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          data-testid="calendar-next-button"
          className="inline-flex items-center gap-1 rounded-full border border-forest/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
        >
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-8 grid grid-cols-7 gap-px border border-forest/10 bg-forest/10">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-parchment px-2 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-sage">
            {d}
          </div>
        ))}
        {cells.map((d, i) => {
          const key = d ? keyFor(d) : null;
          const dayList = key ? byDate[key] || [] : [];
          const isBlocked = key ? blockedSet.has(key) : false;
          return (
            <div
              key={i}
              data-testid={key ? `calendar-day-${key}` : `calendar-blank-${i}`}
              onClick={() => d && setSelectedDay(key)}
              style={isBlocked ? STRIPE : undefined}
              className={`min-h-[110px] p-2 ${d ? "cursor-pointer transition-colors duration-200 hover:bg-parchment" : "bg-parchment/50"} ${d && !isBlocked ? "bg-cream" : ""} ${isBlocked ? "bg-parchment" : ""}`}
            >
              {d && (
                <>
                  <span
                    className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                      key === todayKey ? "bg-gold text-forest" : "text-sage"
                    }`}
                  >
                    {d}
                  </span>
                  <div className="mt-1 space-y-1">
                    {isBlocked && (
                      <div className="flex items-center gap-1.5 rounded border border-forest/15 bg-forest/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-sage" data-testid={`calendar-blocked-${key}`}>
                        <Ban className="h-3 w-3" /> Blocked
                      </div>
                    )}
                    {dayList.map((it) => (
                      <div
                        key={it.id}
                        data-testid={`calendar-chip-${it.id}`}
                        title={`${it.name} — ${it.preferred_time || "any time"} (${it.status})`}
                        className="flex items-center gap-1.5 truncate rounded border border-forest/10 bg-parchment px-2 py-1 text-[10px] font-medium text-forest"
                      >
                        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[it.status] || DOT.new}`} />
                        <span className="truncate">{it.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-5 text-xs text-sage">
        {[
          ["New", DOT.new],
          ["Contacted", DOT.contacted],
          ["Confirmed", DOT.confirmed],
          ["Closed", DOT.closed],
        ].map(([label, dot]) => (
          <span key={label} className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${dot}`} /> {label}
          </span>
        ))}
        <span className="flex items-center gap-2">
          <Ban className="h-3 w-3" /> Blocked day
        </span>
        {noDates > 0 && (
          <span className="italic">{noDates} request{noDates > 1 ? "s" : ""} without a preferred date {noDates > 1 ? "are" : "is"} not shown.</span>
        )}
        <span className="italic">Click any day for details or to block it.</span>
      </div>

      {selectedDay && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-forest-deep/60 px-4 backdrop-blur-sm"
          onClick={() => setSelectedDay(null)}
          data-testid="calendar-day-popup"
        >
          <div className="max-h-[85vh] w-full max-w-md overflow-y-auto border border-gold/40 bg-cream p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-2xl font-medium text-forest" data-testid="calendar-popup-date">
                {formatPreferred(selectedDay, "")}
              </h3>
              <button onClick={() => setSelectedDay(null)} aria-label="Close" data-testid="calendar-popup-close" className="text-sage transition-colors hover:text-forest">
                <X className="h-5 w-5" />
              </button>
            </div>

            {dayBlocked && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest/5 px-4 py-2 text-xs font-semibold text-sage" data-testid="calendar-popup-blocked-note">
                <Ban className="h-3.5 w-3.5" /> This day is blocked — visitors can't request it.
              </p>
            )}

            <div className="mt-6 space-y-4">
              {dayItems.length === 0 ? (
                <p className="text-sm italic text-sage">No appointments requested for this day.</p>
              ) : (
                dayItems.map((it) => (
                  <div key={it.id} className="border border-forest/10 bg-parchment p-5" data-testid={`calendar-popup-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-medium text-forest">{it.name}</p>
                        <p className="mt-0.5 text-xs text-sage">{it.preferred_time || "Any time"}</p>
                        <a href={`mailto:${it.email}`} className="mt-1 block text-xs text-sage underline-offset-2 hover:text-forest hover:underline">
                          {it.email}
                        </a>
                      </div>
                      <span className={`rounded-full border px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] ${STATUS_STYLES[it.status] || STATUS_STYLES.new}`}>
                        {it.status}
                      </span>
                    </div>
                    {it.status !== "confirmed" && it.status !== "closed" && (
                      <button
                        onClick={() => onConfirm(it.id, it.name)}
                        data-testid={`calendar-popup-confirm-${it.id}`}
                        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
                      >
                        <CalendarCheck className="h-3 w-3" /> Confirm Appointment
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3 border-t border-forest/10 pt-6">
              {dayBlocked ? (
                <button
                  onClick={() => onUnblock(selectedDay)}
                  data-testid="calendar-popup-unblock"
                  className="inline-flex items-center gap-1.5 rounded-full bg-forest px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
                >
                  Unblock This Day
                </button>
              ) : (
                <button
                  onClick={() => onBlock(selectedDay)}
                  data-testid="calendar-popup-block"
                  className="inline-flex items-center gap-1.5 rounded-full border border-forest/25 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                >
                  <Ban className="h-3 w-3" /> Block This Day
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, textarea = false, testid }) => {
  const autoRows = textarea ? Math.min(Math.max(3, String(value).split("\n").length + 1), 30) : undefined;
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">{label}</label>
      {textarea ? (
        <textarea rows={autoRows} value={value} onChange={onChange} data-testid={testid} className={`${inputClass} resize-y`} />
      ) : (
        <input type="text" value={value} onChange={onChange} data-testid={testid} className={inputClass} />
      )}
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="border border-forest/10 bg-cream p-8">
    <h2 className="font-display text-2xl font-medium text-forest">{title}</h2>
    <div className="mt-6 grid gap-6">{children}</div>
  </section>
);

function ContentEditor() {
  const { content, reload } = useContent();
  const [form, setForm] = useState(() => structuredClone(content));
  const [saving, setSaving] = useState(false);

  const setPath = (path, value) =>
    setForm((f) => {
      const copy = structuredClone(f);
      let node = copy;
      for (let i = 0; i < path.length - 1; i++) node = node[path[i]];
      node[path[path.length - 1]] = value;
      return copy;
    });

  const save = async () => {
    setSaving(true);
    try {
      await withRefreshRetry(() => api.put("/content", form));
      await reload();
      toast.success("Website content updated — changes are live.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (label, path, opts = {}) => (
    <Field
      label={label}
      value={path.reduce((n, k) => n?.[k] ?? "", form)}
      onChange={(e) => setPath(path, e.target.value)}
      testid={`content-${path.join("-")}`}
      {...opts}
    />
  );

  return (
    <div className="space-y-8" data-testid="content-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-xl text-sm leading-relaxed text-sage">
          Edit the text shown across the website, then save — changes go live immediately. Clearing a field restores the original default text.
        </p>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </button>
      </div>

      <Section title="Homepage Hero">
        {field("Eyebrow", ["hero", "eyebrow"])}
        <div className="grid gap-6 sm:grid-cols-3">
          {field("Headline Line 1", ["hero", "line1"])}
          {field("Headline Line 2", ["hero", "line2"])}
          {field("Headline Line 3 (gold)", ["hero", "line3"])}
        </div>
        {field("Intro Paragraph", ["hero", "subtext"], { textarea: true })}
      </Section>

      <Section title="Homepage About Teaser">
        <div className="grid gap-6 sm:grid-cols-2">
          {field("Heading Line 1", ["aboutTeaser", "heading1"])}
          {field("Heading Line 2 (olive)", ["aboutTeaser", "heading2"])}
        </div>
        {field("Paragraph", ["aboutTeaser", "copy"], { textarea: true })}
      </Section>

      <Section title="Values Row">
        <div className="grid gap-6 sm:grid-cols-2">
          {form.values.map((v, i) => (
            <div key={i} className="border border-forest/10 bg-parchment p-5">
              {field(`Value ${i + 1} — Title`, ["values", i, "title"])}
              <div className="mt-4">{field(`Value ${i + 1} — Text`, ["values", i, "copy"], { textarea: true })}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Call-to-Action Band">
        {field("Eyebrow", ["cta", "eyebrow"])}
        {field("Heading", ["cta", "heading"])}
        {field("Paragraph", ["cta", "copy"], { textarea: true })}
      </Section>

      <Section title="About Page">
        <div className="grid gap-6 sm:grid-cols-2">
          {field("Hero Line 1", ["about", "line1"])}
          {field("Hero Line 2 (gold)", ["about", "line2"])}
        </div>
        {field("Hero Intro", ["about", "intro"], { textarea: true })}
        <div className="grid gap-6 sm:grid-cols-2">
          {field("Jennifer Heading 1", ["about", "jenniferHeading1"])}
          {field("Jennifer Heading 2 (olive)", ["about", "jenniferHeading2"])}
        </div>
        {field("Jennifer Bio — Paragraph 1", ["about", "bio1"], { textarea: true })}
        {field("Jennifer Bio — Paragraph 2", ["about", "bio2"], { textarea: true })}
        <div className="grid gap-6 sm:grid-cols-3">
          {form.about.chapters.map((c, i) => (
            <div key={i} className="border border-forest/10 bg-parchment p-5">
              {field(`Chapter ${i + 1} — Title`, ["about", "chapters", i, "title"])}
              <div className="mt-4">{field(`Chapter ${i + 1} — Text`, ["about", "chapters", i, "copy"], { textarea: true })}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Contact Page & Info">
        <div className="grid gap-6 sm:grid-cols-2">
          {field("Phone (displayed)", ["contact", "phoneDisplay"])}
          {field("Email (displayed)", ["contact", "email"])}
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {field("Hero Line 1", ["contact", "line1"])}
          {field("Hero Line 2 (gold)", ["contact", "line2"])}
        </div>
        {field("Hero Intro", ["contact", "intro"], { textarea: true })}
      </Section>

      <Section title="Footer">
        {field("Tagline", ["footer", "tagline"])}
      </Section>

      <Section title="Experience Behind Rooted (Homepage)">
        {field("Section Heading", ["experience", "heading"])}
        {field("Intro", ["experience", "intro"], { textarea: true })}
        <div className="grid gap-6 sm:grid-cols-2">
          {form.experience.items.map((item, i) => (
            <div key={i} className="border border-forest/10 bg-parchment p-5">
              {field(`Item ${i + 1} — Big Number / Word`, ["experience", "items", i, "stat"])}
              <div className="mt-4">{field(`Item ${i + 1} — Label`, ["experience", "items", i, "label"])}</div>
              <div className="mt-4">{field(`Item ${i + 1} — Details`, ["experience", "items", i, "details"], { textarea: true })}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Core Values (About Page)">
        {form.coreValues.map((v, i) => (
          <div key={i} className="border border-forest/10 bg-parchment p-5">
            {field(`Value ${i + 1} — Title`, ["coreValues", i, "title"])}
            <div className="mt-4">{field(`Value ${i + 1} — Text`, ["coreValues", i, "copy"], { textarea: true })}</div>
          </div>
        ))}
      </Section>

      <Section title="Who We Work Best With (About Page)">
        {field("Heading", ["whoWeServe", "heading"])}
        {field("Intro", ["whoWeServe", "intro"], { textarea: true })}
        {form.whoWeServe.bullets.map((b, i) => (
          <div key={i}>{field(`Bullet ${i + 1}`, ["whoWeServe", "bullets", i])}</div>
        ))}
        {field("Closing Line", ["whoWeServe", "outro"], { textarea: true })}
      </Section>

      <Section title="Legal Pages">
        <p className="text-xs leading-relaxed text-sage">
          Each line is a paragraph. Start a line with "# " to make it a section heading. Have an attorney review any changes before relying on them.
        </p>
        <Field
          label="Privacy Policy"
          value={form.legal.privacy}
          onChange={(e) => setPath(["legal", "privacy"], e.target.value)}
          textarea
          testid="content-legal-privacy"
        />
        <Field
          label="Website Terms of Use"
          value={form.legal.terms}
          onChange={(e) => setPath(["legal", "terms"], e.target.value)}
          textarea
          testid="content-legal-terms"
        />
        <Field
          label="Professional Disclaimer"
          value={form.legal.disclaimer}
          onChange={(e) => setPath(["legal", "disclaimer"], e.target.value)}
          textarea
          testid="content-legal-disclaimer"
        />
      </Section>

      <Section title="Services">
        <p className="text-xs leading-relaxed text-sage">
          For the included / not-included lists, put each item on its own line.
        </p>
        {form.services.map((s, i) => (
          <div key={s.id || i} className="border border-forest/10 bg-parchment p-5" data-testid={`content-service-${s.id || i}`}>
            {field("Service Name", ["services", i, "name"])}
            <div className="mt-4">{field("Short Description", ["services", i, "blurb"], { textarea: true })}</div>
            <div className="mt-4 grid gap-6 sm:grid-cols-2">
              <Field
                label="What's Included (one per line)"
                value={(s.included || []).join("\n")}
                onChange={(e) => setPath(["services", i, "included"], e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
                textarea
                testid={`content-services-${i}-included`}
              />
              <Field
                label="What's Not Included (one per line)"
                value={(s.excluded || []).join("\n")}
                onChange={(e) => setPath(["services", i, "excluded"], e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
                textarea
                testid={`content-services-${i}-excluded`}
              />
            </div>
          </div>
        ))}
      </Section>

      <div className="flex justify-end pb-6">
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button-bottom"
          className="inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

const TABS = [
  ["requests", "Requests", ClipboardList],
  ["calendar", "Calendar", CalendarDays],
  ["content", "Site Content", FileText],
];

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("requests");
  const [items, setItems] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decliningId, setDecliningId] = useState(null);
  const [declineForm, setDeclineForm] = useState({ suggested_date: "", suggested_time: "", note: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reqs, blk] = await withRefreshRetry(() =>
        Promise.all([api.get("/consultations"), api.get("/availability/blocked")])
      );
      setItems(reqs.data);
      setBlocked(blk.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await withRefreshRetry(() => api.patch(`/consultations/${id}`, { status }));
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const confirm = async (id, name) => {
    try {
      const { data } = await withRefreshRetry(() => api.post(`/consultations/${id}/confirm`));
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status: "confirmed" } : it)));
      if (data.email_sent) {
        toast.success(`Appointment confirmed — confirmation email sent to ${name}.`);
      } else {
        toast.warning(`Marked confirmed, but the confirmation email to ${name} could not be sent.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const decline = async (id, name) => {
    try {
      const { data } = await withRefreshRetry(() => api.post(`/consultations/${id}/decline`, declineForm));
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status: "contacted" } : it)));
      setDecliningId(null);
      setDeclineForm({ suggested_date: "", suggested_time: "", note: "" });
      if (data.email_sent) {
        toast.success(`Suggestion sent to ${name}.`);
      } else {
        toast.warning(`Updated, but the email to ${name} could not be sent.`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await withRefreshRetry(() => api.delete(`/consultations/${id}`));
      setItems((list) => list.filter((it) => it.id !== id));
      toast.success("Request deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const blockDay = async (date) => {
    try {
      await withRefreshRetry(() => api.post("/availability/blocked", { date }));
      setBlocked((b) => [...b, date].sort());
      toast.success("Day blocked — visitors can no longer request it.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unblockDay = async (date) => {
    try {
      await withRefreshRetry(() => api.delete(`/availability/blocked/${date}`));
      setBlocked((b) => b.filter((d) => d !== date));
      toast.success("Day unblocked.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const counts = {
    total: items.length,
    new: items.filter((i) => i.status === "new").length,
    contacted: items.filter((i) => i.status === "contacted").length,
    confirmed: items.filter((i) => i.status === "confirmed").length,
    closed: items.filter((i) => i.status === "closed").length,
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-forest/10 bg-forest text-cream">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5 lg:px-10">
          <div className="flex items-center gap-4">
            <Logo size={42} />
            <div>
              <p className="font-display text-lg leading-none">Rooted Admin</p>
              <p className="mt-1 text-xs text-cream/60">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl gap-2 px-6 pb-4 lg:px-10">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                tab === key ? "bg-cream text-forest" : "border border-cream/25 text-cream/80 hover:border-gold hover:text-gold"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
        {tab === "content" && <ContentEditor />}
        {tab === "calendar" && (loading ? (
          <div className="flex justify-center py-24" data-testid="admin-loading">
            <Loader2 className="h-8 w-8 animate-spin text-forest" />
          </div>
        ) : (
          <CalendarView items={items} blocked={blocked} onConfirm={confirm} onBlock={blockDay} onUnblock={unblockDay} />
        ))}

        {tab === "requests" && (
          <>
            <div className="grid grid-cols-2 gap-px border border-forest/10 bg-forest/10 md:grid-cols-5" data-testid="admin-stats">
              {[
                ["Total", counts.total],
                ["New", counts.new],
                ["Contacted", counts.contacted],
                ["Confirmed", counts.confirmed],
                ["Closed", counts.closed],
              ].map(([label, value]) => (
                <div key={label} className="bg-cream p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">{label}</p>
                  <p className="mt-2 font-display text-4xl lining-nums text-forest" data-testid={`admin-stat-${label.toLowerCase()}`}>
                    {value}
                  </p>
                </div>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center py-24" data-testid="admin-loading">
                <Loader2 className="h-8 w-8 animate-spin text-forest" />
              </div>
            ) : items.length === 0 ? (
              <div className="border border-forest/10 bg-parchment p-16 text-center" data-testid="admin-empty-state">
                <p className="font-display text-2xl italic text-forest">No consultation requests yet.</p>
                <p className="mt-3 text-sm text-sage">New submissions from the website will appear here.</p>
              </div>
            ) : (
              <div className="mt-10 space-y-6">
                {items.map((it) => (
                  <article key={it.id} className="border border-forest/10 bg-cream p-8" data-testid={`admin-request-${it.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <h2 className="font-display text-2xl font-medium text-forest" data-testid={`admin-request-name-${it.id}`}>
                          {it.name}
                        </h2>
                        <p className="mt-1 text-sm text-sage">
                          {[it.business, it.service].filter(Boolean).join(" · ") || "General inquiry"}
                        </p>
                      </div>
                      <span
                        className={`rounded-full border px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${STATUS_STYLES[it.status] || STATUS_STYLES.new}`}
                        data-testid={`admin-request-status-${it.id}`}
                      >
                        {it.status}
                      </span>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-x-8 gap-y-2 text-sm text-sage">
                      <a href={`mailto:${it.email}`} className="transition-colors duration-300 hover:text-forest" data-testid={`admin-request-email-${it.id}`}>
                        {it.email}
                      </a>
                      {it.phone && <span data-testid={`admin-request-phone-${it.id}`}>{it.phone}</span>}
                      <span className="text-forest/40">
                        {it.created_at ? new Date(it.created_at).toLocaleString() : ""}
                      </span>
                    </div>

                    {it.preferred_date && (
                      <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-olive/10 px-4 py-2 text-xs font-semibold text-forest" data-testid={`admin-request-slot-${it.id}`}>
                        <CalendarCheck className="h-3.5 w-3.5 text-olive" strokeWidth={1.5} />
                        Requested: {formatPreferred(it.preferred_date, it.preferred_time)}
                      </p>
                    )}

                    <p className="mt-5 border-l-2 border-gold pl-4 text-sm leading-relaxed text-forest/85" data-testid={`admin-request-message-${it.id}`}>
                      {it.message}
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                      {it.status !== "confirmed" && it.status !== "closed" && it.preferred_date && (
                        <button
                          onClick={() => confirm(it.id, it.name)}
                          data-testid={`admin-confirm-${it.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full bg-forest px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
                        >
                          <CalendarCheck className="h-3 w-3" /> Confirm Appointment
                        </button>
                      )}
                      {it.status !== "confirmed" && it.status !== "closed" && it.preferred_date && (
                        <button
                          onClick={() => {
                            setDecliningId(decliningId === it.id ? null : it.id);
                            setDeclineForm({ suggested_date: "", suggested_time: "", note: "" });
                          }}
                          data-testid={`admin-decline-toggle-${it.id}`}
                          className="inline-flex items-center gap-1.5 rounded-full border border-forest/25 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                        >
                          <CalendarX className="h-3 w-3" /> Suggest Different Time
                        </button>
                      )}
                      {it.status === "new" && (
                        <button
                          onClick={() => setStatus(it.id, "contacted")}
                          data-testid={`admin-mark-contacted-${it.id}`}
                          className="rounded-full bg-forest px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest"
                        >
                          Mark Contacted
                        </button>
                      )}
                      {it.status !== "closed" && (
                        <button
                          onClick={() => setStatus(it.id, "closed")}
                          data-testid={`admin-mark-closed-${it.id}`}
                          className="rounded-full border border-forest/25 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                        >
                          Close
                        </button>
                      )}
                      <button
                        onClick={() => remove(it.id)}
                        data-testid={`admin-delete-${it.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-red-800/25 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-red-800 transition-colors duration-300 hover:bg-red-800/5"
                      >
                        <Trash2 className="h-3 w-3" /> Delete
                      </button>
                    </div>

                    {decliningId === it.id && (
                      <div className="mt-6 border-t border-forest/10 pt-6" data-testid={`admin-decline-form-${it.id}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-sage">
                          Suggest an alternative — the visitor will receive this by email
                        </p>
                        <div className="mt-5 grid gap-5 sm:grid-cols-2">
                          <input
                            type="date"
                            value={declineForm.suggested_date}
                            onChange={(e) => setDeclineForm((f) => ({ ...f, suggested_date: e.target.value }))}
                            min={new Date().toISOString().split("T")[0]}
                            data-testid={`admin-decline-date-${it.id}`}
                            className={inputClass}
                          />
                          <select
                            value={declineForm.suggested_time}
                            onChange={(e) => setDeclineForm((f) => ({ ...f, suggested_time: e.target.value }))}
                            data-testid={`admin-decline-time-${it.id}`}
                            className={`${inputClass} cursor-pointer`}
                          >
                            <option value="">Choose a time slot…</option>
                            {TIME_SLOTS.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            placeholder="Optional note to the visitor"
                            value={declineForm.note}
                            onChange={(e) => setDeclineForm((f) => ({ ...f, note: e.target.value }))}
                            data-testid={`admin-decline-note-${it.id}`}
                            className={`${inputClass} sm:col-span-2`}
                          />
                        </div>
                        <div className="mt-5 flex gap-3">
                          <button
                            onClick={() => decline(it.id, it.name)}
                            disabled={!declineForm.suggested_date}
                            data-testid={`admin-decline-send-${it.id}`}
                            className="rounded-full bg-forest px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-gold hover:text-forest disabled:opacity-50"
                          >
                            Send Suggestion
                          </button>
                          <button
                            onClick={() => setDecliningId(null)}
                            data-testid={`admin-decline-cancel-${it.id}`}
                            className="rounded-full border border-forest/25 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-forest transition-colors duration-300 hover:border-gold hover:bg-gold/10"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
      toast.success("Signed out.");
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <Login onSuccess={setUser} />;
}
